import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useFormikContext } from 'formik'
import Grid from '@mui/material/Grid'
import { Box, Button, Typography, useTheme } from '@mui/material'

import { ETranslationNs, ISignInEmailVerification } from 'src/types'

import { useStyles } from '../styles'
import { useSendEmailVerification } from 'src/hooks'
import { AppEvents, ROUTES } from 'src/constants'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'
import { getEncodedQueryParam } from 'src/common/utils'

interface ISignInEmailFormProps {
  isEmailTaken?: boolean
}

const SignInEmailForm = ({ isEmailTaken }: ISignInEmailFormProps) => {
  const [t] = useTranslation()
  const { errors, values, handleChange, isSubmitting } =
    useFormikContext<ISignInEmailVerification>()
  const [searchParams] = useSearchParams()
  const preCreatedUserId = searchParams.get('userId')
  const walletAddress = searchParams.get('address')

  const navigate = useNavigate()

  const { mutateAsync: sendExistingEmailVerification } = useSendEmailVerification()

  const email = getEncodedQueryParam('email')

  const handleSendExisitingEmailVerification = async () => {
    const id = searchParams.get('userId')

    if (!id) {
      const detail = { message: t('sign-in-email-error-message.v') }

      const event = new CustomEvent(AppEvents.HTTP_ERROR, { detail })
      document.dispatchEvent(event)

      return navigate(ROUTES.LOGIN)
    }

    await sendExistingEmailVerification({ email: values.email, id })
  }

  const theme = useTheme()
  const classes = useStyles()

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center">
      <Grid item xs={12} className={classes.containerField}>
        <TextInputWithHelper
          fullWidth
          inputName="email"
          handleChange={handleChange}
          value={values.email}
          error={errors.email ? t(errors.email + '.v') : ''}
          inputPlaceholder={t('account-form-email-input-placeholder.v', {
            ns: ETranslationNs.Form,
          })}
          inputUpperLabel={
            <HelperWrapper
              t={'email-input-label'}
              ns={ETranslationNs.Common}
              isInputLable
              isMandatory
            />
          }
        />
      </Grid>
      {isEmailTaken && (
        <Grid item className={classes.warningTextWrapper}>
          <Typography className={classes.warningText}>
            <HelperWrapper t="sign-in-email-email-taken" />
          </Typography>
          <Button
            onClick={handleSendExisitingEmailVerification}
            className={classes.secondaryButton}
            size="small"
            variant="text"
          >
            <HelperWrapper t="sign-in-email-email-connect" />
          </Button>
        </Grid>
      )}
      <Grid item xs={6} display="flex" flexDirection={'column'} rowGap={2} mt={2}>
        <Button
          type="submit"
          disabled={isSubmitting}
          variant="contained"
          color="secondary"
          className={classes.button}
        >
          {email ? (
            <HelperWrapper t="ssign-in-email-account-exists" />
          ) : (
            <HelperWrapper t="sign-in-email-submit-button-2" />
          )}
        </Button>
      </Grid>

      {!walletAddress && (
        <Box sx={{ display: 'flex', mt: '30px' }}>
          <Button
            component={Link}
            to={ROUTES.LOGIN}
            state={{ userId: preCreatedUserId, provider: 'x' }}
            variant="text"
            size="small"
            sx={{
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 300,
              color: theme.palette.custom.primary,
              textTransform: 'none',
            }}
          >
            <HelperWrapper t="sign-in-email-account-exists" />
          </Button>
        </Box>
      )}
    </Grid>
  )
}

export { SignInEmailForm }
